import React, {
  createContext,
  forwardRef,
  MutableRefObject,
  PropsWithChildren,
  RefObject,
  useContext,
  useRef,
  useMemo,
} from 'react';
import { Header as DefaultHeader } from 'new/elements/Form/Header';
import { FormPageType, FormProps } from 'new/elements/Form/useForm';
import { SliderPage } from 'new/elements/Form/SliderPage';
import { FormLoader } from 'new/elements/Form/FormLoader';
import { FormLeaveConfirmation } from 'new/elements/Form/FormLeaveConfirmation';
import { FormProvider } from 'new/elements/Form/FormProvider';
import cx from 'classnames';

import s from './Form.module.scss';

const PagesContainerContext = createContext<MutableRefObject<any>>({
  current: null,
});

export function usePagesRef() {
  return useContext(PagesContainerContext);
}

const PagesContainer = forwardRef(
  (props: PropsWithChildren<any>, ref: RefObject<HTMLDivElement>) => {
    const { children } = props;
    return (
      <div ref={ref} className={s.pages}>
        {children}
      </div>
    );
  },
);

export const FormComponent = (props: FormProps) => {
  const { children, HeaderComponent, containerClassName, isModal } = props;
  const pagesRef = useRef(null);

  const Header = HeaderComponent || (
    <DefaultHeader
      title={props.title}
      description={props.description}
      hideContextualMenu={isModal}
    />
  );

  const containerStyle = useMemo(() => {
    return cx(s.formContainer, isModal ? s.modal : s.page, containerClassName);
  }, [isModal, containerClassName]);

  return (
    <PagesContainerContext.Provider value={pagesRef}>
      <div className={containerStyle}>
        <div aria-label="form-header-container">{Header}</div>
        <PagesContainer ref={pagesRef}>
          {React.Children.map(children, (child: FormPageType, index) => (
            <SliderPage index={index}>{child}</SliderPage>
          ))}
        </PagesContainer>
        <FormLoader />
        <FormLeaveConfirmation onDismiss={props.onDismiss} />
      </div>
    </PagesContainerContext.Provider>
  );
};

export const Form = (props: FormProps) => {
  const { HeaderComponent, StepsComponent, containerClassName } = props;

  return (
    // @ts-ignore
    <FormProvider {...props}>
      <FormComponent
        isModal={props?.isModal}
        isEditMode={props?.isEditMode}
        onDismiss={props.onDismiss}
        containerClassName={containerClassName}
        HeaderComponent={HeaderComponent}
        StepsComponent={StepsComponent}
      >
        {props.children}
      </FormComponent>
    </FormProvider>
  );
};
